import React, { useState, useEffect } from 'react'
import './styles.scss'
import { config } from './config'
import { checkIfUserWasCorrect } from '../functions'
import value from '*.mp3'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/loader.css'
import { voteComparison, voteQuality, sumReduce, toPercent } from '../functions'

enum RequestStatus {
  ready,
  loading,
  success,
  error
}
interface EstimateQuestionProps {
  questionID: string
  articleID: string
  question: string | JSX.Element
  correctAnswer: string | number
  options?: string[]
  askTheQuestion: (props: askTheQuestionProps) => JSX.Element
  answerTheQuestion: (props: answerTheQuestionProps) => JSX.Element
  reportsuccess: (a: string) => void
  ignoreLocalStorage?: boolean
}

export const EstimateQuestion = ({
  articleID,
  questionID,
  question,
  correctAnswer,
  options,
  askTheQuestion,
  answerTheQuestion,
  reportsuccess,
  ignoreLocalStorage = false
}: EstimateQuestionProps) => {
  /**
   *
   * STATE
   *
   */
  const [questionData, setQuestionData] = useState<questionDataType>({
    articleID: articleID,
    questionID: questionID,
    answer: undefined
  })
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(
    RequestStatus.ready
  )
  const [allEstimates, setAllEstimates] = useState<questionInDB | undefined>(
    undefined
  )
  const [voted, setVoted] = useState<boolean>(false)
  /**
   *
   * EFFECTS
   *
   */
  useEffect(() => {
    if (ignoreLocalStorage) return
    let allreadyanswered = localStorage
      .getItem('estimate-' + articleID + '-' + questionID)
      ?.replace(/(^"|"$)/g, '')
    if (allreadyanswered) {
      // Get data from the database
      fetch(`${config.DB_ENDPOINT}/${articleID}/${questionID}.json`)
        .then(response =>
          response.json().then(values => {
            setAllEstimates(values)
            setQuestionData({
              ...questionData,
              answer: allreadyanswered.replace('.', '_')
            })
            setVoted(true)
            reportsuccess(questionID)
          })
        )
        .catch(error => {
          console.log(error)
        })
    }
  }, [])
  useEffect(() => {
    if (!questionData.answer) return
    submit()
  }, [questionData.answer])
  /**
   *
   * FUNCTIONS
   *
   */
  const updatePreviousValuesWithNewVotes = (
    previousValue: questionInDB,
    questionAnswer: questionDataType
  ) => {
    if (!questionAnswer.answer) return
    let copy = JSON.parse(JSON.stringify(previousValue))
    if (copy[questionAnswer.answer]) copy[questionAnswer.answer]++
    if (!copy[questionAnswer.answer]) copy[questionAnswer.answer] = 1
    return copy
  }
  const writeToDatabase: writeToDBType = (data, previousValue, eTag) => {
    return fetch(`${config.DB_ENDPOINT}/${articleID}/${questionID}.json`, {
      headers: eTag ? { 'if-match': eTag } : undefined,
      method: 'PUT',
      body: JSON.stringify(
        updatePreviousValuesWithNewVotes(previousValue, data)
      )
    }).then(writeResponse =>
      writeResponse.json().then(json => {
        const responseStatus = writeResponse.status

        // Request has failed as data changed before, but luckily we get the new
        // data back right away
        if (responseStatus === 412) {
          const newValue = json
          const newEtag = writeResponse.headers.get('ETag')

          return writeToDatabase(data, newValue, newEtag)
        }

        if (responseStatus === 200) {
          // Save the vote status to local storage
          setAllEstimates(json)

          localStorage.setItem(
            'estimate-' + articleID + '-' + questionID,
            JSON.stringify(data.answer)
          )

          return json
        }
      })
    )
  }
  const pushVotesToDB = (data: questionDataType) => {
    return fetch(`${config.DB_ENDPOINT}/${articleID}/${questionID}.json`, {
      headers: { 'X-Firebase-ETag': 'true' }
    })
      .then(originalValueResponse =>
        originalValueResponse.json().then(previousValue => {
          // yes, this is very optimistic programming
          if (!originalValueResponse.ok) {
            throw Error(originalValueResponse.statusText)
          }

          // etag, used to check whether the values have changed between
          // getting the previous values and writing the new values
          const etag = originalValueResponse.headers.get('ETag')

          // No previous values? Apparently you're the first one to vote
          if (!previousValue) {
            return fetch(
              `${config.DB_ENDPOINT}/${articleID}/${questionID}.json`,
              {
                method: 'PUT',
                body: JSON.stringify({
                  [data.answer + '']: 1
                })
              }
            ).then(writeResponse => {
              if (!writeResponse.ok) {
                throw Error(writeResponse.statusText)
              }

              localStorage.setItem(
                'estimate-' + articleID + '-' + questionID,
                JSON.stringify(data.answer)
              )
              return writeResponse.json()
            })
          }
          return writeToDatabase(data, previousValue, etag)
        })
      )
      .catch((error: Error) => {
        console.log(error.message, error)
      })
  }
  const submit = () => {
    setRequestStatus(RequestStatus.loading)

    pushVotesToDB(questionData)
      .then((downloadedValues: questionInDB) => {
        setAllEstimates(downloadedValues)
        setRequestStatus(RequestStatus.success)
        setVoted(true)
        reportsuccess(questionID)
      })
      .catch((error: Error) => {
        console.log(error.message, error)
        setRequestStatus(RequestStatus.error)
      })
  }
  const handleSubmit = (answer: string) => {
    setQuestionData({ ...questionData, answer: answer.replace('.', '_') })
  }

  return (
    <div className='EstimateQuestion'>
      <h4 className='question'>{question}</h4>

      {/* Ask the Question */}
      {askTheQuestion &&
        !voted &&
        requestStatus === 0 &&
        askTheQuestion({
          questionID,
          question,
          options,
          correctAnswer,
          submit: handleSubmit
        })}

      {/* Loading */}
      {requestStatus === 1 && (
        <div className='ui segment'>
          <div className='ui active inverted dimmer'>
            <div className='ui small text loader'>Antwort wird gesendet...</div>
          </div>
        </div>
      )}

      {/* Answer the Question */}
      {allEstimates && voted && questionData.answer && requestStatus === 2 && (
        <div className='ui raised segment answer_container'>
          {answerTheQuestion({
            questionID,
            question,
            options,
            correctAnswer,
            allAnswers: allEstimates,
            userAnswer: questionData.answer,
            answerCount: +Object.values(allEstimates)
              .map(m => +m)
              .reduce(sumReduce, 0),
            percentSameAsUser: toPercent(
              +allEstimates[questionData.answer],
              +Object.values(allEstimates)
                .map(m => +m)
                .reduce(sumReduce, 0)
            ),
            answeredCorrectly: checkIfUserWasCorrect(
              questionData.answer,
              allEstimates,
              correctAnswer,
              options
            ),
            quality: voteQuality(
              [0, 100],
              +correctAnswer,
              +questionData.answer.replace('_', '.')
            ),
            comparison: voteComparison(
              allEstimates,
              +correctAnswer,
              +questionData.answer.replace('_', '.')
            )
          })}
        </div>
      )}
    </div>
  )
}
